// src/components/UserSpace/EvenementInscriptionUser.js
import React, { useState, useEffect, useRef } from 'react';
import { evenementService } from '../../api';
import { Link } from 'react-router-dom';
import { 
  FaCalendarAlt, 
  FaMapMarkerAlt, 
  FaUsers, 
  FaClock,
  FaTrash,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle,
  FaEuroSign,
  FaDownload
} from 'react-icons/fa';
import html2canvas from 'html2canvas';
import './EvenementInscriptionUser.css';

const EvenementInscriptionUser = () => {
  const [inscriptions, setInscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);

  // Charger les inscriptions
  useEffect(() => {
    loadInscriptions();
  }, []);

  const loadInscriptions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await evenementService.getInscriptions();
      
      console.log('🔍 RÉPONSE COMPLÈTE DE L\'API:', response);
      
      let inscriptionsData = [];
      
      if (Array.isArray(response)) {
        inscriptionsData = response;
      } else if (response && response.data && Array.isArray(response.data)) {
        inscriptionsData = response.data;
      } else if (response && response.inscriptions && Array.isArray(response.inscriptions)) {
        inscriptionsData = response.inscriptions;
      }
      
      console.log('📋 DONNÉES DES INSCRIPTIONS:', inscriptionsData);
      
      setInscriptions(inscriptionsData);
    } catch (err) {
      setError(err.message || 'Erreur lors du chargement des inscriptions');
      console.error('❌ Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  // Se désinscrire d'un événement
  const handleDesinscrire = async (evenementId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir vous désinscrire de cet événement ?')) {
      return;
    }

    try {
      setError(null);
      setSuccessMessage('');
      
      await evenementService.desinscrire(evenementId);
      
      // Mettre à jour la liste
      setInscriptions(prev => prev.filter(inscription => {
        const eventId = inscription.evenement_id || inscription.id;
        return eventId !== evenementId;
      }));
      
      setSuccessMessage('Désinscription réussie !');
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.message || 'Erreur lors de la désinscription');
      console.error('❌ Erreur:', err);
    }
  };

  // Télécharger l'événement en image
  const handleDownloadImage = async (evenementId) => {
    try {
      setDownloadingId(evenementId);
      
      const cardElement = document.getElementById(`event-card-${evenementId}`);
      
      if (!cardElement) {
        throw new Error('Élément non trouvé');
      }

      // Masquer temporairement le bouton de téléchargement pendant la capture
      const downloadBtn = cardElement.querySelector('.download-btn');
      if (downloadBtn) {
        downloadBtn.style.display = 'none';
      }

      const canvas = await html2canvas(cardElement, {
        backgroundColor: '#ffffff',
        scale: 2, // Meilleure qualité
        useCORS: true,
        allowTaint: true,
        logging: false
      });

      // Restaurer le bouton de téléchargement
      if (downloadBtn) {
        downloadBtn.style.display = 'flex';
      }

      // Convertir en image et télécharger
      const image = canvas.toDataURL('image/png', 1.0);
      const link = document.createElement('a');
      link.download = `evenement-${evenementId}-${new Date().toISOString().split('T')[0]}.png`;
      link.href = image;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSuccessMessage('Image téléchargée avec succès !');
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (err) {
      setError('Erreur lors du téléchargement de l\'image');
      console.error('❌ Erreur:', err);
    } finally {
      setDownloadingId(null);
    }
  };

  // Formater la date
  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch (e) {
      return 'Date invalide';
    }
  };

  // Formater l'heure
  const formatTime = (dateString) => {
    try {
      return new Date(dateString).toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'Heure invalide';
    }
  };

  // Vérifier si l'événement est passé
  const isEventPassed = (dateString) => {
    try {
      return new Date(dateString) < new Date();
    } catch (e) {
      return false;
    }
  };

  // Vérifier si l'événement est aujourd'hui
  const isEventToday = (dateString) => {
    try {
      const eventDate = new Date(dateString).toDateString();
      const today = new Date().toDateString();
      return eventDate === today;
    } catch (e) {
      return false;
    }
  };

  // Fonction pour obtenir les données de l'événement
  const getEvenementData = (inscription) => {
    // Si la relation evenement est chargée
    if (inscription.evenement && inscription.evenement.titre) {
      return {
        titre: inscription.evenement.titre,
        description: inscription.evenement.description,
        lieu: inscription.evenement.lieu,
        date: inscription.evenement.date,
        prix: inscription.evenement.prix,
        image: inscription.evenement.image,
        id: inscription.evenement.id
      };
    }
    // Si les données sont directement dans l'inscription (fallback)
    else if (inscription.titre) {
      return {
        titre: inscription.titre,
        description: inscription.description,
        lieu: inscription.lieu,
        date: inscription.date,
        prix: inscription.prix,
        image: inscription.image,
        id: inscription.id
      };
    }
    // Données manquantes - on va devoir les charger
    else {
      return {
        titre: 'Chargement...',
        description: '',
        lieu: '',
        date: '',
        prix: null,
        image: null,
        id: inscription.evenement_id || inscription.id
      };
    }
  };

  // Charger les détails d'un événement manquant
  const loadEvenementDetails = async (evenementId) => {
    try {
      const evenement = await evenementService.getById(evenementId);
      
      // Mettre à jour l'inscription avec les détails de l'événement
      setInscriptions(prev => prev.map(inscription => {
        if (inscription.evenement_id === evenementId || inscription.id === evenementId) {
          return {
            ...inscription,
            evenement: evenement
          };
        }
        return inscription;
      }));
    } catch (err) {
      console.error('❌ Erreur lors du chargement des détails:', err);
    }
  };

  // Charger les détails des événements manquants
  useEffect(() => {
    if (inscriptions.length > 0) {
      inscriptions.forEach(inscription => {
        const evenementData = getEvenementData(inscription);
        if (evenementData.titre === 'Chargement...') {
          loadEvenementDetails(evenementData.id);
        }
      });
    }
  }, [inscriptions]);

  if (loading) {
    return (
      <div className="evenement-inscription-loading">
        <div className="loading-spinner"></div>
        <p>Chargement de vos inscriptions...</p>
      </div>
    );
  }

  return (
    <div className="evenement-inscription-container">
      {/* Flèche de retour fixe */}
      <div className="fixed-back-arrow">
        <Link to="/" className="home-arrow">
          <div className="arrow-icon">←</div>
        </Link>
      </div>

      <div className="evenement-inscription-header">
        <h2>
          <FaCalendarAlt className="header-icon" />
          Mes inscriptions aux événements
        </h2>
        <p className="inscription-count">
          {inscriptions.length} événement{inscriptions.length !== 1 ? 's' : ''} inscrit{inscriptions.length !== 1 ? 's' : ''}
        </p>
      </div>

      {successMessage && (
        <div className="success-message">
          <FaCheckCircle className="success-icon" />
          {successMessage}
        </div>
      )}

      {error && (
        <div className="error-message">
          <FaExclamationTriangle className="error-icon" />
          {error}
        </div>
      )}

      {inscriptions.length === 0 ? (
        <div className="no-inscriptions">
          <FaCalendarAlt className="no-data-icon" />
          <h3>Aucune inscription</h3>
          <p>Vous n'êtes pas encore inscrit à des événements.</p>
        </div>
      ) : (
        <div className="inscriptions-grid">
          {inscriptions.map((inscription, index) => {
            const evenement = getEvenementData(inscription);
            const evenementId = evenement.id;
            
            return (
              <div 
                id={`event-card-${evenementId}`}
                key={inscription.id || evenementId || index} 
                className={`inscription-card ${isEventPassed(evenement.date) ? 'passed' : ''}`}
              >
                {isEventToday(evenement.date) && (
                  <div className="event-badge today">Aujourd'hui</div>
                )}
                
                {isEventPassed(evenement.date) && (
                  <div className="event-badge passed">Terminé</div>
                )}

                <div className="event-image">
                  {evenement.image ? (
                    <img 
                      src={`http://localhost:8000/storage/${evenement.image}`} 
                      alt={evenement.titre} 
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className="event-image-placeholder"
                    style={{ display: evenement.image ? 'none' : 'flex' }}
                  >
                    <FaCalendarAlt />
                  </div>
                </div>

                <div className="event-content">
                  <h3 className="event-title">{evenement.titre}</h3>
                  
                  <div className="event-details">
                    {evenement.date && (
                      <div className="event-detail">
                        <FaClock className="detail-icon" />
                        <span>
                          {formatDate(evenement.date)} à {formatTime(evenement.date)}
                        </span>
                      </div>
                    )}
                    
                    {evenement.lieu && (
                      <div className="event-detail">
                        <FaMapMarkerAlt className="detail-icon" />
                        <span>{evenement.lieu}</span>
                      </div>
                    )}
                    
                    {evenement.prix !== null && evenement.prix !== undefined && (
                      <div className="event-detail">
                        <FaEuroSign className="detail-icon" />
                        <span>{evenement.prix} €</span>
                      </div>
                    )}
                  </div>

                  {evenement.description && (
                    <div className="event-description-section">
                      <h4>Description</h4>
                      <p className="event-description">
                        {evenement.description.length > 200 
                          ? `${evenement.description.substring(0, 200)}...` 
                          : evenement.description
                        }
                      </p>
                    </div>
                  )}

                  {/* Notes de l'utilisateur */}
                  {inscription.notes && (
                    <div className="user-notes">
                      <strong>Mes notes :</strong>
                      <p>{inscription.notes}</p>
                    </div>
                  )}

                  <div className="event-actions">
                    <span className="inscription-date">
                      Inscrit le {formatDate(inscription.created_at)}
                    </span>
                    
                    <div className="action-buttons">
                      <button
                        onClick={() => handleDownloadImage(evenementId)}
                        className="download-btn"
                        disabled={downloadingId === evenementId}
                        title="Télécharger en image"
                      >
                        {downloadingId === evenementId ? (
                          <div className="download-spinner"></div>
                        ) : (
                          <FaDownload />
                        )}
                        {downloadingId === evenementId ? 'Téléchargement...' : 'Télécharger'}
                      </button>
                      
                      {evenement.date && !isEventPassed(evenement.date) && (
                        <button
                          onClick={() => handleDesinscrire(evenementId)}
                          className="desinscrire-btn"
                          title="Se désinscrire"
                        >
                          <FaTrash />
                          Se désinscrire
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EvenementInscriptionUser;